/**
 * Retirement Planning Calculations
 * WP-RETIRE-2: Pure functions for retirement projections
 *
 * Based on docs/spec/retirement-planning.md Section 6
 */

import type { RetirementPlanningInputs, RetirementEvent } from '@/types/retirement';

// ============================================================================
// Types
// ============================================================================

/**
 * @deprecated Use FundingLevel instead. Kept for backward compatibility
 * with video scenes that have their own local copy.
 */
export type RiskLevel = 'low' | 'moderate' | 'high' | 'critical';

/**
 * Funding classification for heatmap display.
 * Three post-retirement levels (WP-RETIRE-5) plus pre-retirement neutral (WP-RETIRE-11).
 */
export type FundingLevel = 'well_funded' | 'adequate' | 'underfunded' | 'pre_retirement';

export interface ProjectionCell {
  age: number;
  returnRate: number; // 0.03, 0.04, 0.05
  projectedWealthCents: number;
  fundingLevel: FundingLevel;
  yearsRemaining: number; // From this age to 95
  yearsCovered: number; // How many years of expenses this wealth covers
  coverageRatio: number; // yearsCovered / yearsRemaining
}

export interface ProjectionGrid {
  cells: ProjectionCell[][]; // rows (return rates) x cols (ages)
  ageColumns: number[]; // filtered by currentAge
  returnRates: number[]; // [0.05, 0.04, 0.03]
}

// ============================================================================
// Constants (from spec Section 2.2)
// ============================================================================

/** Minimum age milestone to show (floor for young users) */
export const MIN_AGE_MILESTONE = 50;

/** Maximum age milestone to show (practical planning horizon) */
export const MAX_AGE_MILESTONE = 90;

/** Milestone interval in years */
export const MILESTONE_INTERVAL = 5;

/**
 * @deprecated Use getAgeColumns() which generates dynamic milestones.
 * Kept for backward compatibility only.
 */
export const AGE_MILESTONES = [65, 70, 75, 80, 85, 90, 95] as const;

/** Fixed return rate scenarios (real return after inflation) */
export const RETURN_RATES = [0.05, 0.04, 0.03] as const;

/**
 * Qualitative labels for return rate rows (WP-RETIRE-5 Change 1).
 * Maps numeric return rate to human-friendly label + tooltip.
 */
export const RETURN_RATE_LABELS: Record<number, { label: string; tooltip: string }> = {
  0.05: {
    label: 'Above Average',
    tooltip: '5% real return after inflation \u2014 an optimistic scenario',
  },
  0.04: {
    label: 'Average',
    tooltip: '4% real return after inflation \u2014 a typical long-term scenario',
  },
  0.03: {
    label: 'Below Average',
    tooltip: '3% real return after inflation \u2014 a conservative scenario',
  },
};

/** Life expectancy assumption for coverage calculations */
export const LIFE_EXPECTANCY = 95;

/**
 * Conservative real growth rate assumed for coverage/risk assessment.
 * Used in calculateSustainableYears to model continued portfolio growth
 * during retirement. Lower than projection rates (3-5%) to provide
 * a conservative safety assessment.
 */
export const COVERAGE_GROWTH_RATE = 0.02;

// ============================================================================
// Social Security Start Age
// ============================================================================

/**
 * Earliest age Social Security retirement benefits can begin. The SSA pays no
 * retirement benefit before 62, so no projection may count one earlier -
 * whatever start age is stored and whatever the retirement age is.
 *
 * Mirrored by MIN_SOCIAL_SECURITY_START_AGE in
 * services/core/app/schemas/retirement.py, which rejects lower ages on save.
 */
export const MIN_SOCIAL_SECURITY_START_AGE = 62;

/** Latest age Social Security claiming can be deferred to. */
export const MAX_SOCIAL_SECURITY_START_AGE = 70;

/**
 * The age Social Security actually starts in a projection.
 *
 * An unset start age means "when I retire" (see RetirementPlanningInputs), but
 * never before MIN_SOCIAL_SECURITY_START_AGE. Without the floor, someone
 * retiring at 55 with no start age set would be paid from 55, and rows saved
 * before the minimum existed (the old input allowed 50) would be too.
 */
export function effectiveSocialSecurityStartAge(
  startAge: number | null | undefined,
  retirementAge: number
): number {
  return Math.max(startAge ?? retirementAge, MIN_SOCIAL_SECURITY_START_AGE);
}

/**
 * Raise a stored Social Security start age that is below the minimum up to it,
 * leaving an unset age unset (it still means "when I retire").
 *
 * Applied to every save: the API rejects ages below the minimum, and every save
 * resends the whole plan, so a user whose row predates the minimum could
 * otherwise not save ANY field.
 */
export function clampSocialSecurityStartAge(
  startAge: number | null | undefined
): number | null | undefined {
  return startAge == null
    ? startAge
    : Math.max(startAge, MIN_SOCIAL_SECURITY_START_AGE);
}

// ============================================================================
// Core Calculation (spec Section 6.1)
// ============================================================================

/**
 * Calculate projected real wealth at a target age.
 * All monetary values in cents. Returns are "real" (inflation-adjusted).
 *
 * Algorithm from spec Section 6.1:
 * - Accumulation phase (currentAge -> retirementAge): grow wealth + add savings
 * - Distribution phase (retirementAge -> targetAge): grow wealth - withdrawals
 *
 * WP-RETIRE-8: Optional events support.
 * Events are applied based on calendar year (derived from birthYear).
 *
 * @param currentAge - User's current age
 * @param retirementAge - Target retirement age
 * @param targetAge - Age to project wealth to
 * @param currentNetWorthCents - Current net worth in cents
 * @param annualSavingsCents - Annual savings rate in cents
 * @param retirementIncomeCents - Total annual retirement income (SS + pension + other) in cents
 * @param retirementExpensesCents - Expected annual expenses in retirement in cents
 * @param realReturn - Real rate of return (e.g., 0.03, 0.04, 0.05)
 * @param events - Optional array of retirement events (WP-RETIRE-8)
 * @param birthYear - Required when events are provided to convert calendar years to ages
 * @param incomeSources - Optional per-source breakdown with start ages. Pass it
 *   whenever it is known: without it the combined retirementIncomeCents is
 *   paid from retirementAge, so Social Security's age-62 floor cannot apply.
 * @returns Projected wealth in cents at target age
 */
export function calculateProjectedWealth(
  currentAge: number,
  retirementAge: number,
  targetAge: number,
  currentNetWorthCents: number,
  annualSavingsCents: number,
  retirementIncomeCents: number,
  retirementExpensesCents: number,
  realReturn: number,
  events?: RetirementEvent[],
  birthYear?: number,
  incomeSources?: {
    socialSecurityAnnualCents: number;
    pensionAnnualCents: number;
    otherRetirementIncomeCents: number;
    socialSecurityStartAge?: number | null;
    pensionStartAge?: number | null;
    otherIncomeStartAge?: number | null;
  },
): number {
  let wealth = currentNetWorthCents;

  for (let age = currentAge; age < targetAge; age++) {
    if (age < retirementAge) {
      // Accumulation phase: earn returns + add savings
      wealth = wealth * (1 + realReturn) + annualSavingsCents;
    } else {
      // Distribution phase: earn returns - withdraw (expenses - income)
      let effectiveIncome: number;
      if (incomeSources) {
        // Per-source start age: each source only applies when age >= its start age.
        // Taken whenever the breakdown is supplied, even with no start ages set,
        // because Social Security's floor (MIN_SOCIAL_SECURITY_START_AGE) can
        // start it later than the retirement age - which the combined total
        // below cannot express.
        effectiveIncome = 0;
        const ssStart = effectiveSocialSecurityStartAge(
          incomeSources.socialSecurityStartAge,
          retirementAge
        );
        const penStart = incomeSources.pensionStartAge ?? retirementAge;
        const otherStart = incomeSources.otherIncomeStartAge ?? retirementAge;
        if (age >= ssStart) effectiveIncome += incomeSources.socialSecurityAnnualCents;
        if (age >= penStart) effectiveIncome += incomeSources.pensionAnnualCents;
        if (age >= otherStart) effectiveIncome += incomeSources.otherRetirementIncomeCents;
      } else {
        effectiveIncome = retirementIncomeCents;
      }
      const annualWithdrawal = retirementExpensesCents - effectiveIncome;
      wealth = wealth * (1 + realReturn) - annualWithdrawal;
    }

    // Apply events for this year (WP-RETIRE-8)
    if (events && events.length > 0 && birthYear != null) {
      const calendarYear = birthYear + age;
      for (const event of events) {
        if (event.eventType === 'lump_sum') {
          // Lump sum: apply only in the specific year
          if (event.year === calendarYear) {
            wealth += event.amountCents;
          }
        } else if (event.eventType === 'cash_flow') {
          // Cash flow: apply annually from year to endYear (inclusive)
          const endYear = event.endYear ?? event.year;
          if (calendarYear >= event.year && calendarYear <= endYear) {
            wealth += event.amountCents;
          }
        }
      }
    }

    // Wealth cannot go negative (for display purposes)
    wealth = Math.max(wealth, 0);
  }

  return Math.round(wealth);
}

// ============================================================================
// Funding Classification (WP-RETIRE-5 Change 2)
// ============================================================================

/**
 * @deprecated Use classifyFunding() instead. Kept for backward compatibility.
 */
export function classifyRisk(
  coverageRatio: number,
  projectedWealthCents: number
): RiskLevel {
  if (projectedWealthCents <= 0) return 'critical';
  if (coverageRatio >= 1.5) return 'low';
  if (coverageRatio >= 1.0) return 'moderate';
  if (coverageRatio >= 0.5) return 'high';
  return 'critical';
}

/**
 * Classify funding level at a given age based on pre-computed coverage ratio.
 * Three-level system (WP-RETIRE-5 Change 2):
 *
 * | Coverage Ratio | Funding Level  |
 * |----------------|----------------|
 * | > 125%         | well_funded    |
 * | 75-125%        | adequate       |
 * | < 75% or $0    | underfunded    |
 *
 * @param coverageRatio - Pre-computed coverage ratio (yearsCovered / yearsRemaining)
 * @param projectedWealthCents - Projected wealth at this age in cents
 * @returns Funding level classification
 */
export function classifyFunding(
  coverageRatio: number,
  projectedWealthCents: number
): FundingLevel {
  if (projectedWealthCents <= 0) {
    return 'underfunded';
  }

  if (coverageRatio > 1.25) {
    return 'well_funded'; // Green: >125% coverage
  } else if (coverageRatio >= 0.75) {
    return 'adequate'; // Yellow: 75-125% coverage
  } else {
    return 'underfunded'; // Red: <75% coverage
  }
}

/**
 * Calculate how many years wealth sustains a given net withdrawal rate,
 * assuming continued real growth on remaining assets.
 *
 * Uses the annuity depletion formula:
 *   N = ln(D / (D - W*r)) / ln(1+r)
 *
 * where W = wealth, D = annual net withdrawal, r = real growth rate.
 *
 * @param wealthCents - Current portfolio value in cents
 * @param annualNetWithdrawalCents - Annual net withdrawal (expenses - income) in cents
 * @param growthRate - Real growth rate (default: COVERAGE_GROWTH_RATE = 0.02)
 * @returns Number of years wealth lasts, or Infinity if it never depletes
 */
export function calculateSustainableYears(
  wealthCents: number,
  annualNetWithdrawalCents: number,
  growthRate: number = COVERAGE_GROWTH_RATE
): number {
  if (wealthCents <= 0) return 0;
  if (annualNetWithdrawalCents <= 0) return Infinity;
  if (growthRate <= 0) return wealthCents / annualNetWithdrawalCents;

  const returnOnWealth = wealthCents * growthRate;
  if (annualNetWithdrawalCents <= returnOnWealth) return Infinity;

  return Math.log(annualNetWithdrawalCents / (annualNetWithdrawalCents - returnOnWealth))
       / Math.log(1 + growthRate);
}

// ============================================================================
// Grid Building
// ============================================================================

/**
 * Generate age columns for the projection grid.
 *
 * Rules:
 * 1. Start from the next 5-year milestone after currentAge
 * 2. But never start earlier than MIN_AGE_MILESTONE (50)
 * 3. Continue in 5-year increments up to MAX_AGE_MILESTONE (90)
 *
 * Examples:
 * - currentAge 45 → [50, 55, 60, 65, 70, 75, 80, 85, 90]
 * - currentAge 55 → [60, 65, 70, 75, 80, 85, 90]
 * - currentAge 72 → [75, 80, 85, 90]
 * - currentAge 88 → [90]
 * - currentAge 90 → []
 *
 * @param currentAge - User's current age
 * @returns Array of age milestones for projection columns
 */
export function getAgeColumns(currentAge: number): number[] {
  // Calculate next 5-year milestone after current age
  // The +1 ensures we get the NEXT milestone, not current (e.g., age 55 → 60, not 55)
  const nextMilestone =
    Math.ceil((currentAge + 1) / MILESTONE_INTERVAL) * MILESTONE_INTERVAL;

  // Start from max of minimum milestone or next milestone
  const startAge = Math.max(MIN_AGE_MILESTONE, nextMilestone);

  // Generate milestones from start to max
  const columns: number[] = [];
  for (let age = startAge; age <= MAX_AGE_MILESTONE; age += MILESTONE_INTERVAL) {
    columns.push(age);
  }

  return columns;
}

/**
 * Build complete projection grid for display.
 * Creates 3 rows (5%, 4%, 3%) x N columns (ages > currentAge).
 *
 * WP-RETIRE-8: Optional events and birthYear for event-aware projections.
 *
 * @param inputs - Retirement planning inputs from API
 * @param netWorthCents - Total net worth in cents (including adjustments)
 * @param events - Optional retirement events (WP-RETIRE-8)
 * @param birthYear - Optional birth year for event calendar year conversion
 * @returns Complete projection grid with all cells calculated
 */
/**
 * Compute effective annual retirement income at a given age,
 * accounting for per-source start ages.
 */
function getEffectiveIncomeAtAge(inputs: RetirementPlanningInputs, age: number): number {
  const retirementAge = inputs.retirementAge;
  if (age < retirementAge) return 0;

  const ssStart = effectiveSocialSecurityStartAge(
    inputs.socialSecurityStartAge,
    retirementAge
  );
  const penStart = inputs.pensionStartAge ?? retirementAge;
  const otherStart = inputs.otherIncomeStartAge ?? retirementAge;

  let income = 0;
  if (age >= ssStart) income += inputs.socialSecurityAnnualCents;
  if (age >= penStart) income += inputs.pensionAnnualCents;
  if (age >= otherStart) income += inputs.otherRetirementIncomeCents;
  return income;
}

export function buildProjectionGrid(
  inputs: RetirementPlanningInputs,
  netWorthCents: number,
  events?: RetirementEvent[],
  birthYear?: number,
): ProjectionGrid {
  const ageColumns = getAgeColumns(inputs.currentAge);

  // Calculate total retirement income (annual) — used as fallback for calculateProjectedWealth
  const retirementIncomeCents =
    inputs.socialSecurityAnnualCents +
    inputs.pensionAnnualCents +
    inputs.otherRetirementIncomeCents;

  const incomeSources = {
    socialSecurityAnnualCents: inputs.socialSecurityAnnualCents,
    pensionAnnualCents: inputs.pensionAnnualCents,
    otherRetirementIncomeCents: inputs.otherRetirementIncomeCents,
    socialSecurityStartAge: inputs.socialSecurityStartAge,
    pensionStartAge: inputs.pensionStartAge,
    otherIncomeStartAge: inputs.otherIncomeStartAge,
  };

  // Build cells for each return rate x age combination
  const cells: ProjectionCell[][] = RETURN_RATES.map((returnRate) => {
    return ageColumns.map((targetAge) => {
      const projectedWealthCents = calculateProjectedWealth(
        inputs.currentAge,
        inputs.retirementAge,
        targetAge,
        netWorthCents,
        inputs.currentSavingsAnnualCents,
        retirementIncomeCents,
        inputs.retirementExpensesAnnualCents,
        returnRate,
        events,
        birthYear,
        incomeSources,
      );

      // Pre-retirement phase: neutral classification, skip coverage assessment (WP-RETIRE-11)
      if (targetAge < inputs.retirementAge) {
        return {
          age: targetAge,
          returnRate,
          projectedWealthCents,
          fundingLevel: 'pre_retirement' as FundingLevel,
          yearsRemaining: LIFE_EXPECTANCY - targetAge,
          yearsCovered: 0,
          coverageRatio: 0,
        };
      }

      // Coverage: use effective income AT this target age (start-age-aware)
      const effectiveIncomeAtAge = getEffectiveIncomeAtAge(inputs, targetAge);
      const netWithdrawalAtAge = Math.max(
        inputs.retirementExpensesAnnualCents - effectiveIncomeAtAge,
        0
      );

      const yearsRemaining = LIFE_EXPECTANCY - targetAge;
      const yearsCovered = calculateSustainableYears(
        projectedWealthCents,
        netWithdrawalAtAge,
        COVERAGE_GROWTH_RATE
      );
      const coverageRatio = yearsCovered === Infinity
        ? 999
        : yearsCovered / Math.max(yearsRemaining, 1);

      const fundingLevel = classifyFunding(coverageRatio, projectedWealthCents);

      return {
        age: targetAge,
        returnRate,
        projectedWealthCents,
        fundingLevel,
        yearsRemaining,
        yearsCovered,
        coverageRatio,
      };
    });
  });

  return {
    cells,
    ageColumns,
    returnRates: [...RETURN_RATES],
  };
}

// ============================================================================
// Goal Planning Calculator (WP-RETIRE-5 Change 4)
// ============================================================================

/**
 * Binary-search for the minimum monthly savings needed to achieve "well_funded"
 * status at a given retirement age, for each return rate scenario.
 *
 * "Well Funded" means coverageRatio > 1.25 at the goal retirement age.
 *
 * @param inputs - Current retirement planning inputs (expenses, income, ages, etc.)
 * @param netWorthCents - Current total net worth in cents
 * @param goalRetirementAge - The target retirement age to evaluate
 * @returns Required monthly savings for each scenario, or null if inputs are invalid
 */
export function calculateRequiredMonthlySavings(
  inputs: RetirementPlanningInputs,
  netWorthCents: number,
  goalRetirementAge: number,
): { belowAverage: number; average: number; aboveAverage: number } | null {
  if (goalRetirementAge <= inputs.currentAge || goalRetirementAge > 90) {
    return null;
  }

  // Build income sources for start-age-aware projection
  const retirementIncomeCents =
    inputs.socialSecurityAnnualCents +
    inputs.pensionAnnualCents +
    inputs.otherRetirementIncomeCents;

  const incomeSources = {
    socialSecurityAnnualCents: inputs.socialSecurityAnnualCents,
    pensionAnnualCents: inputs.pensionAnnualCents,
    otherRetirementIncomeCents: inputs.otherRetirementIncomeCents,
    socialSecurityStartAge: inputs.socialSecurityStartAge,
    pensionStartAge: inputs.pensionStartAge,
    otherIncomeStartAge: inputs.otherIncomeStartAge,
  };

  // Use start-age-aware income at goal retirement age for coverage
  const effectiveIncomeAtGoal = getEffectiveIncomeAtAge(
    { ...inputs, retirementAge: goalRetirementAge },
    goalRetirementAge
  );
  const annualNetWithdrawalCents = Math.max(
    inputs.retirementExpensesAnnualCents - effectiveIncomeAtGoal,
    0
  );

  const yearsRemaining = LIFE_EXPECTANCY - goalRetirementAge;

  /**
   * For a given return rate, binary search on monthly savings to find
   * the minimum that achieves well_funded (coverageRatio > 1.25).
   * Search range: $0 - $100K/mo, precision: $10/mo, max 30 iterations.
   * Returns monthly savings in dollars, or Infinity if not achievable.
   */
  function findRequiredSavings(returnRate: number): number {
    // First check: is it already well_funded with $0 savings?
    if (isWellFundedWithSavings(0, returnRate)) {
      return 0;
    }

    // Check: is it achievable even with $100K/mo?
    const maxMonthlyCents = 100_000 * 100; // $100K/mo in cents
    if (!isWellFundedWithSavings(maxMonthlyCents, returnRate)) {
      return Infinity;
    }

    // Binary search
    let lo = 0;
    let hi = maxMonthlyCents;
    for (let i = 0; i < 30; i++) {
      const mid = Math.floor((lo + hi) / 2);
      if (isWellFundedWithSavings(mid, returnRate)) {
        hi = mid;
      } else {
        lo = mid;
      }
      // Precision: $10/mo = 1000 cents
      if (hi - lo <= 1000) break;
    }

    // Return hi (first value that works) converted to dollars/month
    return Math.ceil(hi / 100);
  }

  function isWellFundedWithSavings(monthlySavingsCents: number, returnRate: number): boolean {
    const annualSavingsCents = monthlySavingsCents * 12;

    const projectedWealth = calculateProjectedWealth(
      inputs.currentAge,
      goalRetirementAge,
      goalRetirementAge,
      netWorthCents,
      annualSavingsCents,
      retirementIncomeCents,
      inputs.retirementExpensesAnnualCents,
      returnRate,
      undefined,  // events
      undefined,  // birthYear
      incomeSources,
    );

    if (projectedWealth <= 0) return false;

    const yearsCovered = calculateSustainableYears(
      projectedWealth,
      annualNetWithdrawalCents,
      COVERAGE_GROWTH_RATE
    );

    const coverageRatio = yearsCovered === Infinity
      ? 999
      : yearsCovered / Math.max(yearsRemaining, 1);

    return coverageRatio > 1.25;
  }

  return {
    belowAverage: findRequiredSavings(0.03),
    average: findRequiredSavings(0.04),
    aboveAverage: findRequiredSavings(0.05),
  };
}
